package one.healthium.onboarding.ui.components

import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.statusBarsPadding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.util.fastForEachIndexed
import loco.ONBOARDING_SIGN_IN_BUTTON
import one.healthium.onboarding.domain.models.SelectableOption
import one.healthium.onboarding.ui.UserProfileUiModel
import one.healthium.onboarding.ui.bindPlaceholderValues
import one.healthium.ui.components.CtaButton
import one.healthium.ui.compose.components.AppButtonHeight
import one.healthium.ui.compose.components.AppText
import one.healthium.ui.compose.components.AppTextButton
import one.healthium.ui.compose.theme.AppTheme
import org.jetbrains.compose.resources.stringResource

@Composable
internal fun SelectableContent(
    modifier: Modifier = Modifier,
    title: String?,
    subtitle: String?,
    options: List<SelectableOption>,
    selectedOptions: List<SelectableOption>,
    singleChoice: Boolean,
    ctaTitle: String?,
    userState: UserProfileUiModel,
    onOptionsChanged: (List<SelectableOption>) -> Unit,
    onNextClicked: () -> Unit,
    signInEnabled: Boolean = false,
    onSignInClicked: () -> Unit,
    additionalSelectedText: String? = null,
) {
    Box {
        Column(
            modifier = modifier
                .fillMaxSize()
                .statusBarsPadding()
                .padding(horizontal = 24.dp)
                .verticalScroll(rememberScrollState()),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            AppText(
                text = title?.bindPlaceholderValues(userState),
                style = AppTheme.textStyles.header,
                color = AppTheme.colors.text100,
                textAlign = TextAlign.Center,
            )
            Spacer(Modifier.height(24.dp))
            AppText(
                text = subtitle?.bindPlaceholderValues(userState),
                style = AppTheme.textStyles.subTitle,
                color = AppTheme.colors.text100,
                textAlign = TextAlign.Center,
            )
            Spacer(Modifier.height(24.dp))
            Spacer(Modifier.weight(0.7f))
            val hasAll = selectedOptions.any { it.key == "all" }
            options.fastForEachIndexed { index, option ->
                if (index > 0) Spacer(Modifier.height(16.dp))
                // If "all" is selected, only "all" chip is visually active
                val isSelected = if (hasAll) {
                    option.key == "all"
                } else {
                    selectedOptions.any { it.key == option.key }
                }
                SelectableItem(
                    option = option,
                    selected = isSelected,
                    userState = userState,
                    additionalSelectedText = additionalSelectedText,
                    onClick = {
                        val updatedOptions = if (singleChoice) {
                            listOf(option)
                        } else if (option.key == "all") {
                            // Toggle all: select all concrete options + "all", or clear
                            if (hasAll) emptyList()
                            else options.filter { it.key != "other" }
                        } else if (hasAll) {
                            // "all" was active → only the tapped item stays selected
                            listOf(option)
                        } else {
                            val actuallySelected = selectedOptions.any { it.key == option.key }
                            if (actuallySelected) selectedOptions - option else selectedOptions + option
                        }
                        onOptionsChanged(updatedOptions)
                        if (singleChoice) {
                            onNextClicked()
                        }
                    }
                )
            }
            Spacer(Modifier.height(60.dp))
            Spacer(Modifier.weight(1f))
            Spacer(Modifier.height(AppButtonHeight))
            if (signInEnabled) {
                Spacer(Modifier.height(36.dp))
            }
        }
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .navigationBarsPadding()
                .padding(horizontal = 38.dp)
                .align(Alignment.BottomCenter),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            CtaButton(
                modifier = Modifier.fillMaxWidth(),
                onClick = onNextClicked,
                enabled = selectedOptions.isNotEmpty(),
                text = ctaTitle,
            )
            if (signInEnabled) {
                AppTextButton(
                    modifier = Modifier.testTag("sign_in_button"),
                    onClick = onSignInClicked,
                ) {
                    Text(stringResource(loco.Res.string.ONBOARDING_SIGN_IN_BUTTON))
                }
            }
            Spacer(Modifier.height(24.dp))
        }
    }
}
